import { Injectable } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';

export interface DashboardRoleParameter {
  key: string;
  value: string;
}

export interface DashboardParameterCatalogOption {
  key: string;
  label: string;
  type: string;
}

interface DashboardParameterObjectItem {
  label: string;
  type: string;
}

export interface DashboardRoleEditDialogFieldVm {
  control_name: 'key' | 'value';
  label: string;
  input_type: 'select' | 'text' | 'number' | 'date';
  placeholder?: string;
  options?: Array<{ value: string; view_value: string }>;
}

export interface DashboardRoleEditDialogRowVm {
  form_group: FormGroup;
  fields: DashboardRoleEditDialogFieldVm[];
  meta_label: string;
  meta_type: string;
}

export interface DashboardRoleEditDialogVm {
  can_add_parameters: boolean;
  rows: DashboardRoleEditDialogRowVm[];
}

@Injectable({
  providedIn: 'root'
})
export class DashboardRoleEditDialogService {

  
  build_catalog_options(
    parameters_objects: Record<string, DashboardParameterObjectItem> = {},
    role_parameters: DashboardRoleParameter[] = []
  ): DashboardParameterCatalogOption[] {
    // Convierte el objeto del catálogo a una lista estable para la UI.
    const catalog_options = Object.entries(parameters_objects ?? {}).map(([key, item]) => ({
      key,
      label: item?.label ?? key,
      type: item?.type ?? 'string'
    }));

    const existing_keys = new Set(catalog_options.map((option) => option.key));
    const fallback_options = (role_parameters ?? [])
      .filter((parameter) => !!parameter?.key)
      .map((parameter) => ({
        key: parameter.key,
        label: parameter.key,
        type: 'string'
      }))
      .filter((option) => !existing_keys.has(option.key));

    const deduped = new Map<string, DashboardParameterCatalogOption>();
    [...catalog_options, ...fallback_options].forEach((option) => {
      if (!deduped.has(option.key)) {
        deduped.set(option.key, option);
      }
    });

    return Array.from(deduped.values());
  }

  build_view_model(
    parameters_array: FormArray<FormGroup>,
    catalog_options: DashboardParameterCatalogOption[]
  ): DashboardRoleEditDialogVm {
    // Arma filas reactivas para que el HTML solo itere datos listos.
    const selected_keys = new Set(
      parameters_array.controls
        .map((control) => String(control.get('key')?.value ?? '').trim())
        .filter((key) => key.length > 0)
    );

    const rows = parameters_array.controls.map((form_group) => {
      const current_key = String(form_group.get('key')?.value ?? '').trim();
      const current_option = catalog_options.find((option) => option.key === current_key);
      const available_options = catalog_options.filter((option) => {
        if (option.key === current_key) {
          return true;
        }
        return !selected_keys.has(option.key);
      });
      const value_input_type = this.resolve_value_input_type(current_option?.type);

      return {
        form_group,
        meta_label: current_option?.label ?? current_key,
        meta_type: current_option?.type ?? 'string',
        fields: [
          {
            control_name: 'key',
            label: 'Clave',
            input_type: 'select' as const,
            options: available_options.map((option) => ({
              value: option.key,
              view_value: option.label
            }))
          },
          {
            control_name: 'value',
            label: 'Valor',
            input_type: value_input_type,
            placeholder: this.resolve_placeholder(value_input_type),
            options:
              value_input_type === 'select'
                ? [
                    { value: 'true', view_value: 'true' },
                    { value: 'false', view_value: 'false' }
                  ]
                : undefined
          }
        ]
      } satisfies DashboardRoleEditDialogRowVm;
    });

    return {
      can_add_parameters: catalog_options.length > 0,
      rows
    };
  }

  private resolve_value_input_type(type?: string): 'text' | 'number' | 'date' | 'select' {
    // Resuelve el control de valor según el tipo del catálogo.
    switch (String(type ?? 'string')) {
      case 'number':
        return 'number';
      case 'date':
        return 'date';
      case 'boolean':
        return 'select';
      default:
        return 'text';
    }
  }

  private resolve_placeholder(input_type: 'text' | 'number' | 'date' | 'select'): string {
    // Devuelve un placeholder corto para el control visible.
    switch (input_type) {
      case 'number':
        return '123';
      case 'date':
        return 'YYYY-MM-DD';
      case 'select':
        return 'Selecciona una opción';
      default:
        return 'Ingresa un valor';
    }
  }
}
